/**
 * useCallingPhone.ts
 *
 * iOS CallingPhonePresenter.swift を Jotai + カスタムフックで移植。
 *
 * iOS → Web の対応:
 *   viewDidLoad()                   → useEffect mount
 *   viewWillDisappear()             → useEffect cleanup
 *   tappedBackButton()              → onTapBack()
 *   tappedHangUpPhoneButton()       → onTapHangUp()
 *   didChangeLibraryStatus()        → handleStatusChange()
 *   startChangeDesignTimer()        → changeDesignTimerRef setTimeout
 *   startCallTimeTimer()            → callDurationTimerRef setInterval
 *   startAutoHangUpTimer()          → autoHangUpTimerRef setTimeout
 *   router.popViewController()      → navigate(-1)
 *   router.popTopViewController()   → navigate("/reception/loading")
 *   router.pushCompleteReception()  → navigate("/reception/complete")
 *   router.pushFailedReception()    → navigate("/reception/loading")
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom, useSetAtom } from "jotai";

import { sipManager } from "~/utils/SipManager";
import { CallLibraryStatus, HangUpReasonType } from "~/utils/sip.types";
import type { SipCallInfo } from "~/utils/sip.types";
import type { CallInfoEntity } from "~/utils/receptionDB.types";
import { runReceptionBack } from "~/utils/receptionNavigation";
import {
  callStatusAtom,
  isCallingAtom,
  callDurationAtom,
  hangUpReasonAtom,
} from "~/utils/sipAtoms";
import { ROUTE } from "~/constants/routes";

/** changeDesignTimer の待機時間（秒）: この時間後に電話を切るボタンを表示する */
const CHANGE_DESIGN_TIMER_SECONDS = 3;

/** 通話画面のデザイン状態 */
export type CallingPhoneDesignType =
  | "idle"
  | "waitingWithoutButton"
  | "waitingWithButton"
  | "calling";

export interface UseCallingPhoneOptions {
  /** 発信先内線番号 */
  extensionNumber: string;
  /** IndexedDB から取得した通話設定エンティティ */
  callInfo: CallInfoEntity;
  /** 来訪履歴登録成功時の遷移先 */
  completePath?: string;
  /**
   * 来訪履歴登録 API 処理（Phase 7 で実装）。
   * 提供されない場合は直接 /reception/loading へ遷移する。
   */
  onPostVisitHistory?: () => Promise<void>;
}

export interface UseCallingPhoneResult {
  /** 現在の通話画面デザイン状態 */
  designType: CallingPhoneDesignType;
  /** 通話時間表示文字列（例: "01:23"） */
  callDuration: string;
  /** 戻るボタン押下ハンドラ */
  onTapBack: () => void;
  /** 電話を切るボタン押下ハンドラ */
  onTapHangUp: () => void;
  /** 発信ボタン押下ハンドラ */
  onTapCall: () => void;
}

/**
 * CallInfoEntity (IndexedDB形式) から SipCallInfo (SIP.js設定形式) に変換する。
 * iOS専用フィールド（clientPort / audioSessionMode 等）は除外する。
 */
export function toSipCallInfo(entity: CallInfoEntity): SipCallInfo {
  return {
    serverAddress: entity.serverAddress,
    serverPort: entity.serverPort,
    name: entity.name,
    password: entity.password,
    phoneNumber: entity.phoneNumber,
    displayName: entity.displayName,
    callDisconnectTime: entity.callDisconnectTime,
    expiresValue: entity.expiresValue,
    secondsRetryAfterFail: entity.secondsRetryAfterFail,
    ptimeMs: entity.ptimeMs,
    turnServerUrl: entity.turnServerUrl,
    turnUsername: entity.turnUsername,
    turnPassword: entity.turnPassword,
  };
}

/**
 * SIP通話画面のロジックフック。
 * iOS CallingPhonePresenter.swift と1対1に近い設計で移植。
 */
export function useCallingPhone({
  extensionNumber,
  callInfo,
  completePath,
  onPostVisitHistory,
}: UseCallingPhoneOptions): UseCallingPhoneResult {
  const navigate = useNavigate();
  const [designType, setDesignType] = useState<CallingPhoneDesignType>("idle");

  // Jotai atoms
  const setCallStatus = useSetAtom(callStatusAtom);
  const setIsCalling = useSetAtom(isCallingAtom);
  const [callDurationDisplay, setCallDuration] = useAtom(callDurationAtom);
  const setHangUpReason = useSetAtom(hangUpReasonAtom);

  // タイマーRef
  const changeDesignTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoHangUpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callDurationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callStartTimeRef = useRef<Date | null>(null);

  /**
   * hangUpReason / isCalling を ref で保持する。
   * SipManager のコールバック（クロージャ内）から最新値を参照するために必要。
   */
  const hangUpReasonRef = useRef<HangUpReasonType>(HangUpReasonType.other);
  const isCallingRef = useRef<boolean>(false);
  const disconnectFailureStatusRef = useRef<CallLibraryStatus | null>(null);

  /**
   * regOk 後に call() を1回だけ発信するためのフラグ。
   * ユーザーが発信ボタンを押したことを示す。
   */
  const hasCalledRef = useRef<boolean>(false);

  /**
   * REGISTER 完了（regOk）を受信済みかどうかのフラグ。
   * 発信ボタン押下時に登録が完了していれば即 call() する。
   */
  const isRegisteredRef = useRef<boolean>(false);

  /**
   * INVITE を開始済みかどうかのフラグ。
   * regOk が複数回来ても発信は1回だけにする。
   */
  const hasStartedCallRef = useRef<boolean>(false);

  // extensionNumber を ref で保持（handleStatusChange クロージャから参照）
  const extensionNumberRef = useRef(extensionNumber);

  // onPostVisitHistory を ref で保持してクロージャの stale 問題を防ぐ
  const onPostVisitHistoryRef = useRef(onPostVisitHistory);

  // 完了画面パスを ref で保持してクロージャの stale 問題を防ぐ
  const completePathRef = useRef(completePath);

  useEffect(() => {
    extensionNumberRef.current = extensionNumber;
    onPostVisitHistoryRef.current = onPostVisitHistory;
    completePathRef.current = completePath;
  });

  /**
   * 全タイマーを停止する。
   * iOS: stopAutoHangUpTimer + stopCallTimeTimer + stopChangeDesignTimer 相当。
   */
  const stopAllTimers = useCallback(() => {
    if (changeDesignTimerRef.current !== null) {
      clearTimeout(changeDesignTimerRef.current);
      changeDesignTimerRef.current = null;
    }
    if (autoHangUpTimerRef.current !== null) {
      clearTimeout(autoHangUpTimerRef.current);
      autoHangUpTimerRef.current = null;
    }
    if (callDurationTimerRef.current !== null) {
      clearInterval(callDurationTimerRef.current);
      callDurationTimerRef.current = null;
    }
  }, []);

  /**
   * 強制切断処理。
   * iOS: forceEndCall() 相当（タイマー停止 + SipManager.disconnect）。
   */
  const forceEndCall = useCallback(() => {
    stopAllTimers();
    sipManager.disconnect();
  }, [stopAllTimers]);

  /**
   * disconnect ステータス時の画面遷移処理。
   * iOS: Presenter の case .disconnect 内ロジック相当。
   */
  const handleDisconnectNavigation = useCallback(() => {
    const reason = hangUpReasonRef.current;
    const wasCalling = isCallingRef.current;
    const failureStatus = disconnectFailureStatusRef.current;

    disconnectFailureStatusRef.current = null;

    isCallingRef.current = false;
    setIsCalling(false);

    if (callDurationTimerRef.current !== null) {
      clearInterval(callDurationTimerRef.current);
      callDurationTimerRef.current = null;
    }

    if (reason === HangUpReasonType.tappedBackButton) {
      navigate(-1);
      return;
    }

    if (reason === HangUpReasonType.callFail) {
      if (
        failureStatus === CallLibraryStatus.busy ||
        failureStatus === CallLibraryStatus.callFail
      ) {
        navigate(`${ROUTE.RECEPTION.ERROR}?code=A2004`);
      } else {
        navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } });
      }
      return;
    }

    // tappedHangUpButton or other
    if (!wasCalling) {
      navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } });
      return;
    }
    const postHistory = onPostVisitHistoryRef.current;
    if (postHistory) {
      postHistory()
        .then(() => navigate(completePathRef.current ?? ROUTE.RECEPTION.COMPLETE))
        .catch(() => navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } }));
    } else {
      navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } });
    }
  }, [navigate, setIsCalling]);

  /** connect 受信時の処理 (iOS: case .connect 相当) */
  const handleConnect = useCallback(() => {
    isCallingRef.current = true;
    setIsCalling(true);

    if (changeDesignTimerRef.current !== null) {
      clearTimeout(changeDesignTimerRef.current);
      changeDesignTimerRef.current = null;
    }
    setDesignType("calling");

    const disconnectTimeSec = callInfo.callDisconnectTime;
    if (disconnectTimeSec > 0) {
      autoHangUpTimerRef.current = setTimeout(() => {
        autoHangUpTimerRef.current = null;
        forceEndCall();
      }, disconnectTimeSec * 1000);
    }

    callStartTimeRef.current = new Date();
    callDurationTimerRef.current = setInterval(() => {
      const start = callStartTimeRef.current;
      if (!start) return;
      const elapsed = Math.floor((Date.now() - start.getTime()) / 1000);
      const min = Math.floor(elapsed / 60);
      const sec = elapsed % 60;
      setCallDuration(`${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`);
    }, 1000);
  }, [callInfo.callDisconnectTime, forceEndCall, setCallDuration, setIsCalling]);

  const startOutgoingCall = useCallback(() => {
    if (hasStartedCallRef.current) return;

    hasStartedCallRef.current = true;
    sipManager.call(extensionNumberRef.current);

    changeDesignTimerRef.current = setTimeout(() => {
      changeDesignTimerRef.current = null;
      if (!isCallingRef.current) {
        setDesignType("waitingWithButton");
      }
    }, CHANGE_DESIGN_TIMER_SECONDS * 1000);
  }, []);

  /**
   * SipManager ステータス変更コールバック。
   * iOS: didChangeLibraryStatus() 相当。
   *
   * ★ regOk で発信する理由:
   *   iOS の Linphone は setup() 後に内部キューイングして登録完了後に発信するが、
   *   SIP.js はキューイングしない。setup() と同時に call() すると
   *   トランスポート未接続で即 callFail になるため、regOk 受信後に発信する。
   */
  const handleStatusChange = useCallback(
    (status: CallLibraryStatus) => {
      setCallStatus(status);

      switch (status) {
        case CallLibraryStatus.regOk:
          // REGISTER 完了を記録
          isRegisteredRef.current = true;
          // ユーザーがすでに発信ボタンを押していた場合のみ発信する
          if (hasCalledRef.current) {
            startOutgoingCall();
          }
          break;

        case CallLibraryStatus.connect:
          handleConnect();
          break;

        case CallLibraryStatus.busy:
        case CallLibraryStatus.callFail:
        case CallLibraryStatus.notFound:
          if (changeDesignTimerRef.current !== null) {
            clearTimeout(changeDesignTimerRef.current);
            changeDesignTimerRef.current = null;
          }
          disconnectFailureStatusRef.current = status;
          hangUpReasonRef.current = HangUpReasonType.callFail;
          setHangUpReason(HangUpReasonType.callFail);
          break;

        case CallLibraryStatus.disconnect:
          handleDisconnectNavigation();
          break;

        case CallLibraryStatus.cantUse:
          navigate(`${ROUTE.RECEPTION.ERROR}?code=A2005`);
          break;

        default:
          break;
      }
    },
    [
      navigate,
      handleConnect,
      handleDisconnectNavigation,
      startOutgoingCall,
      setCallStatus,
      setHangUpReason,
      setDesignType,
    ]
  );

  // ============================================================
  // マウント / アンマウント  (iOS viewDidLoad / viewWillDisappear 相当)
  // ============================================================
  useEffect(() => {
    /**
     * canceled フラグ: React StrictMode 対策。
     * 開発環境では StrictMode がエフェクトを 2 回実行する
     * (mount → cleanup → mount)。
     * getUserMedia は非同期なので、cleanup が走った後に
     * 1 回目の Promise が解決して setup() を呼んでしまう。
     * canceled=true になっていれば setup を無視することで
     * UA が 2 重に起動するのを防ぐ。
     */
    let canceled = false;

    // Jotai atoms をリセット
    setCallStatus(CallLibraryStatus.none);
    setIsCalling(false);
    setCallDuration("00:00");
    setHangUpReason(HangUpReasonType.other);
    hangUpReasonRef.current = HangUpReasonType.other;
    isCallingRef.current = false;
    disconnectFailureStatusRef.current = null;
    hasCalledRef.current = false;
    isRegisteredRef.current = false;
    hasStartedCallRef.current = false;

    // マイク権限リクエスト（ブラウザは HTTPS + getUserMedia が必要）
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then(() => {
        // cleanup 済みなら何もしない（StrictMode 対策）
        if (canceled) return;
        // SipManager セットアップのみ行う。
        // 発信 (call) は regOk ステータス受信後に handleStatusChange 内で行う。
        // ※ SIP.js はトランスポート接続前に invite() すると即 callFail になるため
        //   Linphone のようなキューイングが存在しないことへの対処。
        sipManager.setup(toSipCallInfo(callInfo), handleStatusChange);
      })
      .catch(() => {
        if (canceled) return;
        // マイク権限拒否 → 受付失敗画面
        navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } });
      });

    // クリーンアップ
    return () => {
      canceled = true;
      stopAllTimers();
      sipManager.stop();
      setCallStatus(CallLibraryStatus.none);
      setCallDuration("00:00");
    };
    // 初回マウント時のみ実行
  }, []); // mount/unmount のみ

  // ============================================================
  // ユーザー操作ハンドラ
  // ============================================================

  /** 戻るボタン押下 (iOS: tappedBackButton) */
  const onTapBack = useCallback(() => {
    hangUpReasonRef.current = HangUpReasonType.tappedBackButton;
    setHangUpReason(HangUpReasonType.tappedBackButton);
    forceEndCall();
    runReceptionBack({
      kind: "router-history",
      navigate,
    });
  }, [forceEndCall, setHangUpReason]);

  /** 電話を切るボタン押下 (iOS: tappedHangUpPhoneButton) */
  const onTapHangUp = useCallback(() => {
    hangUpReasonRef.current = HangUpReasonType.tappedHangUpButton;
    setHangUpReason(HangUpReasonType.tappedHangUpButton);
    forceEndCall();
  }, [forceEndCall, setHangUpReason]);

  /** 発信ボタン押下: idle → waitingWithoutButton へ遷移し発信を開始する */
  const onTapCall = useCallback(() => {
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;
    setDesignType("waitingWithoutButton");

    if (isRegisteredRef.current) {
      // すでに REGISTER 完了済みなら即発信
      startOutgoingCall();
    }
    // else: regOk 受信時に発信する
  }, [startOutgoingCall]);

  return {
    designType,
    callDuration: callDurationDisplay,
    onTapBack,
    onTapHangUp,
    onTapCall,
  };
}
